import KeyIcon from "@/assets/icons/KeyIcon";
import GameResellPriceTag from "@/components/compatibility/GameResellPriceTag";
import { useCurrency } from "@/hooks/useCurrency";
import { useCanBuyGameKey } from "@/hooks/useCanBuyGameKey";
import { cn } from "@/libs/utils";
import { GameResell } from "@/models/GameResell";
import ContentsService from "@/services/ContentsService";
import { BhapticsContent } from "@/store/reducers/contents";
import { SupportTypeUtil } from "@/utils/SupportTypeUtil";
import DeviceUtil from "@/utils/DeviceUtil";
import { useTrans } from "@/utils/useTrans";
import { Badge } from "@/v2-components/ui/badge";
import { Button } from "@/v2-components/ui/button";
import CartFilledIcon from "@/v2-components/core/icon/cart-filled-icon";
import MetaColoredIcon from "@/v2-components/core/icon/meta-colored-icon";
import OculusIcon from "@/v2-components/core/icon/oculus-icon";
import PCPlatformIcon from "@/v2-components/core/icon/pc-platform-icon";
import PSVRIcon from "@/v2-components/core/icon/ps-vr-icon";
import SimRacingIcon from "@/v2-components/core/icon/sim-racing-icon";
import SteamColoredIcon from "@/v2-components/core/icon/steam-colored-icon";
import { SwitchCase } from "@/v2-components/switch-case";
import { createContext, forwardRef, Fragment, useContext } from "react";
import NextImage from "@/components/shared/NextImage";
import { GameUtil } from "@/utils/GameUtil";
import PicoIcon from "@/v2-components/core/icon/PicoIcon";
import SideQuestIcon from "@/v2-components/core/icon/SideQuestIcon";
import ViveIcon from "@/v2-components/core/icon/vive-icon";

type GameCard = {
  gameResell?: GameResell;
} & BhapticsContent;

interface GameCardContext {
  game: GameCard;
  unavailable: string;
  isLoggedIn?: boolean;
}

const GameCardContext = createContext<GameCardContext | null>(null);

const GameCardProvider = GameCardContext.Provider;

// DO NOT Export
const useGameCardContext = () => {
  const context = useContext(GameCardContext);
  if (!context) {
    throw new Error(
      "GameCard compound components cannot be rendered outside the GameCard component",
    );
  }
  return context;
};

/**
 ************************* GameCardRoot *************************
 */
interface GameCardRootProps {
  game: GameCard;
  isLoggedIn?: boolean;
  children: React.ReactNode;
  className?: string;
}
function GameCardRoot({
  game,
  children,
  className,
  isLoggedIn,
}: GameCardRootProps) {
  const unavailable = GameUtil.getTempUnavailablePlatforms(
    game.supportedPlatforms,
  ).join(", ");

  return (
    <GameCardProvider
      value={{
        game,
        unavailable,
        isLoggedIn,
      }}
    >
      <div className={cn("flex flex-col gap-3", className)}>{children}</div>
    </GameCardProvider>
  );
}

/**
 ************************* GameCard Image *************************
 */
interface GameImageProps {
  className?: string;
  direction?: "horizontal" | "vertical";
  showBadge?: boolean;
  children?: React.ReactNode;
}
const GameImage = forwardRef<HTMLImageElement, GameImageProps>(
  (
    { className, children, direction = "horizontal", showBadge = true },
    ref,
  ) => {
    const { game } = useGameCardContext();
    // 상태 배지(New / Upcoming) 라벨 번역용. 키는 `games` namespace 에 있다.
    const { t } = useTrans(["games"]);

    return (
      <div
        ref={ref}
        className={cn(
          direction === "horizontal"
            ? "aspect-game-card-horizontal"
            : "aspect-game-card-vertical",
          "group relative shrink-0 overflow-hidden rounded-[8px]",
          className,
        )}
      >
        {/**
         * ★**썸네일 호버 확대는 여기 한 곳이다** (사용자 요청 2026-09-09: *"게임 썸네일쪽에는
         * 공통으로 적용"*). 위 래퍼가 이미 `group` 과 `overflow-hidden` 을 갖고 있어, 안쪽
         * 이미지만 키우면 **프레임은 그대로 두고 그림만** 확대된다.
         *
         * ⚠️ 래퍼(`className` prop)에 걸면 안 된다 — 프레임 자체가 커져 이웃을 밀고, 둘 다
         * 걸면 **1.05² 로 이중 확대**된다. 종전에 래퍼로 걸던 5곳을 이 커밋에서 걷었는데,
         * **그 5곳이 같은 표기가 아니었다** (merge-base `18016adca` 실측):
         *
         * ```
         *   DisplayNewGames · DisplayUpcomingGames · PcUpcomingAndNew   hover-scale         (전 폭)
         *   GameCardItem · DisplayWeeklyGames                           laptop:hover-scale  (≥1024)
         * ```
         *
         * ⚠️ `DisplayWeeklyGames` 는 **지금 없다**(#5689 가 삭제 · 2026-09-16). 위 표는
         * merge-base `18016adca` 시점의 실측이라 그대로 둔다 — 지우면 「원래 4곳이었다」로
         * 읽혀 이 커밋이 걷은 범위가 작아 보인다.
         *
         * 여기 중앙 클래스는 맨 `hover-scale` 이라 **뒤 둘은 <1024 에서 없던 확대가 새로
         * 생긴다 — 그게 의도다.** 「공통으로 적용」이 요청이었고, base 리포 관례도 맨 쪽이
         * 다수다(맨 14 : `laptop:` 6, 같은 커밋 실측). 브레이크포인트를 되돌리면
         * `game-thumb-hover-zoom` 가드가 빨간불이 된다.
         *
         * `.hover-scale` 은 `group-hover:` 라 조상에 `.group` 이 있을 때만 발동한다 — 그
         * 조상이 위 래퍼 자신이므로 소비처가 따로 배선할 것이 없다.
         *
         * ## `will-change-transform` 비용은 쟀다 — 차이 없음
         *
         * 이 유틸은 호버와 무관하게 **항상** `will-change` 를 건다. 적용 범위가 이 커밋으로
         * 게임 화면 5곳 → `<GameCard.Image>` 렌더 사이트 20곳(파일 12개)으로 늘어서,
         * 카드를 페이징 없이 다 그리는 주 그리드가 걱정 대상이었다.
         *
         * `/games/vr/games/` 1440(`will-change:transform` 요소 **70개**)에서 같은 탭 A/B —
         * `.hover-scale{will-change:auto!important}` 를 주입해 대조군을 만들고(주입 후 남은
         * 요소 **0개**로 적용 확인), 각 2.5초 균일 스크롤 293프레임:
         *
         * ```
         *          p50    p95    max     >33ms   JS heap
         *   있음   8.3    9.3    49.1    1건     111 MB
         *   없음   8.3    9.3    49.3    1건     111 MB
         * ```
         *
         * ⚠️ 이 수치는 **프레임 타이밍과 JS 힙**이다 — GPU 컴포지팅 레이어 메모리는 JS 에서
         * 못 읽어서 안 쟀다. 그 축이 필요해지면 DevTools 의 레이어 패널로 봐야 한다.
         */}
        <NextImage
          alt={`${game.name} image`}
          className="hover-scale object-cover"
          src={
            direction === "horizontal"
              ? game.thumbnailWideLink
              : game.thumbnailLink
          }
          placeholder="blur"
          blurDataURL="/placeholder.png"
          sizes={"(max-width: 500px) 100vw, 25vw"}
          fill
        />
        {showBadge && (
          <div className="le absolute inset-0 flex gap-1 p-2">
            {game.badges.map((badge) => {
              return (
                <Badge
                  key={badge}
                  // ⚠️ variant 는 **원문**으로 판정한다 — 번역된 라벨로 하면 매칭이 깨진다.
                  variant={badge.toLocaleLowerCase() as "new" | "best"}
                  size="xs"
                  // ⚠️ `normal-case` 로 공용 base 의 `capitalize` 를 덮는다 (#5165).
                  // CSS `capitalize` 는 **단어마다** 첫 글자를 올려서, 라벨이 `New`/`Upcoming`
                  // 한 단어일 때는 무해했지만 번역값은 두 단어다 — de "Bald Unterstützt" ·
                  // fr "Bientôt Compatible" · es "Pronto Compatible" 처럼 해당 언어가 쓰지 않는
                  // title case 가 된다. `badge.tsx` base 에서 빼면 다른 variant 까지 영향이
                  // 가므로, 호출처 우선인 `cn` 특성을 이용해 여기서 덮는다(docs/pitfalls.md #8).
                  //
                  // `whitespace-nowrap` 은 보험이다. `size="xs"` 가 `h-4` 고정인데 줄바꿈이
                  // 일어나면 텍스트가 배지 박스 **밖으로** 넘친다(실측: 2줄 = 24px vs h-4 = 16px).
                  // 지금 라벨은 전부 짧아 한 줄에 들어가지만, 나중에 긴 번역이 들어오면 조용히
                  // 다시 깨진다. nowrap 이면 최악의 경우 부모 `overflow-hidden` 이 가로로 자를
                  // 뿐이라 실패 양상이 낫다.
                  className="z-10 whitespace-nowrap normal-case"
                >
                  {/* 서버 `badges`(상태 배지: New / Upcoming)를 번역해 렌더한다 (#5165).
                      종전엔 원문을 그대로 찍어 한국어 화면에 `Upcoming` 이 그대로 나갔고,
                      같은 게임(Warzone)이 위 섹션에선 "지원 예정"(`UpcomingBadge`)인데 하단
                      그리드에선 `Upcoming` 이라 한 화면에 두 표기가 공존했다.
                      키가 없는 값이 오면 `t()` 가 키를 그대로 돌려줘 영문으로 폴백한다.
                      ⚠️ 지원 타입 배지(Mod / AI Powered / Native)는 `GameTitle` 의
                      `getBadgeText` 경로이고 **영문이 설계**다 — 여기서 건드리지 않는다
                      (docs/game-support-type-policy.md §1). */}
                  {t(badge)}
                </Badge>
              );
            })}
          </div>
        )}
        {children}
      </div>
    );
  },
);
GameImage.displayName = "GameImage";

/**
 ************************* GameCard Resell Banner *************************
 */
interface GameCardResellBannerProps {
  className?: string;
  onClick: () => void;
  short?: boolean;
}
function GameCardResellBanner({
  className,
  onClick,
  short = false,
}: GameCardResellBannerProps) {
  const { t } = useTrans(["gnb"]);
  const { game, isLoggedIn } = useGameCardContext();
  const currency = useCurrency();
  // 판매 제외 국가 · 이 통화 가격 — 판정은 `GameUtil.canBuyGameKey` 한 곳(형제 `ShortResellBanner` · `GameSeller` 와 같다)
  const canBuy = useCanBuyGameKey(game.gameResell);

  if (!canBuy) {
    return null;
  }

  const discountPercentile = GameUtil.getMaxDiscountPercentile(
    game.gameResell,
    currency,
  );

  const discountApplied = t("{{percent}}% Off Applied", {
    ns: "gnb",
    percent: discountPercentile,
    defaultValue: `${discountPercentile}% Off Applied`,
  });
  const discountCoupon = t("{{percent}}% Off Coupon", {
    ns: "gnb",
    percent: discountPercentile,
    defaultValue: `${discountPercentile}% Off Coupon`,
  });
  const couponText = short
    ? `${discountPercentile}%`
    : isLoggedIn
      ? discountApplied
      : discountCoupon;

  return (
    <div
      className={cn(
        "transition-discrete absolute bottom-0 flex h-[23px] w-full items-center justify-around overflow-hidden bg-[linear-gradient(90deg,#FF0096_0%,#FF6600_100%)] text-white transition-[height,display] smd:group-hover:h-[40px] smd:group-hover:bg-blue-60 smd:group-hover:bg-none",
        className,
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Button
        variant="blue-fill"
        size="sm"
        className="hidden w-full smd:group-has-[:hover]:flex"
        onClick={onClick}
      >
        <span>{t("Add")}</span>
        <CartFilledIcon className="size-4.5" />
      </Button>
      <div
        className={cn(
          "flex items-center gap-2 smd:group-has-[:hover]:hidden",
          short ? "text-xs" : "text-base",
        )}
      >
        <KeyIcon className={short ? "size-4" : "size-5"} />
        <span>{couponText}</span>
      </div>
    </div>
  );
}

function GameCardShortResellBanner({
  className,
  onClick,
  short = false,
}: GameCardResellBannerProps) {
  const { t } = useTrans(["gnb"]);
  const { game, isLoggedIn } = useGameCardContext();
  const currency = useCurrency();
  // 존재만 보면 가격 없는 키(번들 전용 · #6053)에 「0% Off」 배너가 뜬다 — `ResellBanner` 와 같은 판정
  const canBuy = useCanBuyGameKey(game.gameResell);

  const discountPercentile = GameUtil.getMaxDiscountPercentile(
    game.gameResell,
    currency,
  );

  const discountApplied = t("{{percent}}% Off", {
    ns: "gnb",
    percent: discountPercentile,
    defaultValue: `${discountPercentile}% Off`,
  });
  const discountCoupon = t("{{percent}}% Off", {
    ns: "gnb",
    percent: discountPercentile,
    defaultValue: `${discountPercentile}% Off`,
  });
  const couponText = short
    ? `${discountPercentile}%`
    : isLoggedIn
      ? discountApplied
      : discountCoupon;

  if (!canBuy) {
    return null;
  }

  return (
    <div
      className={cn(
        "transition-discrete absolute bottom-0 flex h-[23px] w-full items-center justify-around overflow-hidden bg-[linear-gradient(90deg,#FF0096_0%,#FF6600_100%)] text-white transition-[height,display] smd:group-hover:h-[40px] smd:group-hover:bg-blue-60 smd:group-hover:bg-none",
        className,
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Button
        variant="blue-fill"
        size="sm"
        className="hidden w-full smd:group-has-[:hover]:flex"
        onClick={onClick}
      >
        <span>{t("Add")}</span>
        <CartFilledIcon className="size-4.5" />
      </Button>
      <div
        className={cn(
          "flex items-center gap-2 smd:group-has-[:hover]:hidden",
          short ? "text-xs" : "text-base",
        )}
      >
        <KeyIcon className={short ? "size-4" : "size-5"} />
        <span>{couponText}</span>
      </div>
    </div>
  );
}

/**
 ************************* GameCard Title *************************
 */
interface GameCardTitleProps {
  className?: string;
  numbering?: number;
}
function GameTitle({ className, numbering }: GameCardTitleProps) {
  const { game } = useGameCardContext();
  const badgeVariant = SupportTypeUtil.getBadgeVariant(game.supportedPlatforms);
  const badgeText = SupportTypeUtil.getBadgeText(game.supportedPlatforms);

  return (
    <div
      className={cn(
        "flex items-center gap-2 text-base font-600 text-coolgray-90 laptop:text-lg",
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-x-1">
          {numbering !== undefined && (
            <h4 className="text-xl font-700 text-coolgray-30">{numbering}</h4>
          )}
          <Badge variant={badgeVariant} className="whitespace-nowrap" size="sm">
            {badgeText}
          </Badge>
        </div>
      </div>
      <p className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
        {game.name}
      </p>
    </div>
  );
}

/**
 ************************* GameCard Platforms *************************
 */
interface GamePlatformsProps {
  className?: string;
  simple?: boolean;
}
const PLATFORM_ICONS = {
  "Steam VR": SteamColoredIcon,
  "Meta Quest": MetaColoredIcon,
  "Meta Rift": OculusIcon,
  PSVR2: PSVRIcon,
  PC: PCPlatformIcon,
  "Sim Racing": SimRacingIcon,
  Pico: PicoIcon,
  SideQuest: SideQuestIcon,
  VIVE: ViveIcon,
};
function GamePlatforms({ className, simple = false }: GamePlatformsProps) {
  const { game } = useGameCardContext();

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 text-sm capitalize text-coolgray-70 laptop:text-base",
        className,
      )}
    >
      {game.supportedPlatforms
        .filter((p) => p.enabled)
        .map((p, i) => {
          const platformName = ContentsService.friendlyPlatformName(p.platform);
          const IconComp = PLATFORM_ICONS[platformName];
          return (
            <div
              className={cn("flex items-center gap-1")}
              key={`${p.platform}-${i}`}
            >
              {!simple && IconComp && <IconComp className="h-5 w-5" />}
              <span className="whitespace-nowrap">{platformName}</span>
            </div>
          );
        })}
    </div>
  );
}

/**
 ************************* GameCard Devices *************************
 */
interface GameDevicesProps {
  className?: string;
  type?: "text" | "icon";
  itemClassName?: string;
}
function GameDevices({
  className,
  type = "text",
  itemClassName,
}: GameDevicesProps) {
  const { t } = useTrans(["games"]);
  const { game, unavailable } = useGameCardContext();

  return (
    <div className={cn("text-sm text-coolgray-60", className)}>
      {unavailable ? (
        <p className="overflow-hidden text-ellipsis text-nowrap text-red-60">
          {"!"}{" "}
          {t("Temporarily unavailable on {{platforms}}", {
            platforms: unavailable,
            defaultValue: `Temporarily unavailable on ${unavailable}`,
          })}
        </p>
      ) : (
        <div className="flex flex-wrap items-start gap-2">
          {game.compatibleDevices.map((device) => {
            const IconComp = DeviceUtil.getIcon(device);
            return (
              <SwitchCase
                key={device}
                value={type}
                caseBy={{
                  text: (
                    <p
                      key={device}
                      className={cn(
                        "flex flex-wrap items-start gap-x-2 rounded bg-coolgray-10 px-2 py-1",
                        itemClassName,
                      )}
                    >
                      {DeviceUtil.getFriendlyName(device)}
                    </p>
                  ),
                  icon: IconComp ? <IconComp className="h-4 w-4" /> : null,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 ************************* GameCard Resell *************************
 */
interface GameResellProps {
  onClick: () => void;
  className?: string;
  simple?: boolean;
  verticalPrices?: boolean;
}
function GameSeller({
  className,
  onClick,
  simple = false,
  verticalPrices = false,
}: GameResellProps) {
  const { game } = useGameCardContext();
  // 가격 없는 키에 「Add」 버튼을 그리지 않는다 — `ResellBanner` 와 같은 판정
  const canBuy = useCanBuyGameKey(game.gameResell);

  if (!canBuy) {
    return null;
  }

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div
        className={cn(
          "flex flex-col gap-0 laptop:flex-row laptop:items-center laptop:gap-2",
          verticalPrices && "flex-col laptop:flex-col",
        )}
      >
        {game.gameResell?.platformList.map((platformPrice) => (
          <Fragment key={platformPrice.platform}>
            <GameResellPriceTag platformPrice={platformPrice} />
            <div
              className={cn(
                "hidden h-3 w-px bg-coolgray-20 last:hidden laptop:block",
                verticalPrices && "hidden laptop:hidden",
              )}
            />
          </Fragment>
        ))}
      </div>

      <Button
        size={"sm"}
        shape="pill"
        variant="blue-fill"
        className={cn("flex laptop:hidden", simple ? "h-10 w-10" : "pl-6 pr-4")}
        onClick={onClick}
      >
        {!simple && <span>Add</span>}
        <CartFilledIcon className="h-4.5 w-4.5" />
      </Button>
    </div>
  );
}

const GameCard = Object.assign(GameCardRoot, {
  Image: GameImage,
  ResellBanner: GameCardResellBanner,
  ShortResellBanner: GameCardShortResellBanner,
  Title: GameTitle,
  Platforms: GamePlatforms,
  Devices: GameDevices,
  Resell: GameSeller,
});

export default GameCard;
